import { ExtendedTarificationFormData } from '@/types/benefits/tarification/TarificationFormData';
import { Label } from '@/components/ui/label';
import PlusIcon from '@/assets/icons/plus.svg?react';
import {
  ArrayPath,
  type Control,
  FieldArray,
  Path,
  useFieldArray,
  useFormContext,
} from 'react-hook-form';
import StyledButton from '../styled/StyledButton';
import StyledInput from '../styled/StyledInput';
import { BenefitType } from '@/types/benefits/benefitTypes';
import { useMemo } from 'react';
import { useTranslation } from '@/i18n';

const NestedTarifRepartition = <
  FieldsType extends ExtendedTarificationFormData,
>({
  control,
  tarifIndex,
  tarifType,
  customTarif = false,
  isViewMode = false,
  type = BenefitType.ACTIVITY,
}: {
  control: Control<FieldsType>;
  tarifIndex: number;
  tarifType?: 'par-prestation' | 'par-personne';
  customTarif?: boolean;
  isViewMode?: boolean;
  type?: BenefitType;
}) => {
  const { t } = useTranslation();

  const fieldName =
    `tarifs.${tarifIndex}.pricePartitions` as ArrayPath<FieldsType>;

  const { fields, remove, append } = useFieldArray({
    control,
    name: fieldName,
  });
  const { watch } = useFormContext();

  const repartitionValues = watch(fieldName);

  const getToValue = (index: number, add = 0) => {
    return repartitionValues && repartitionValues[index]?.to
      ? Number(repartitionValues[index]?.to) + add
      : undefined;
  };

  const getFromValue = (index: number, add = 0) => {
    return repartitionValues && repartitionValues[index]?.from
      ? Number(repartitionValues[index]?.from) + add
      : undefined;
  };

  const label = useMemo(() => {
    switch (type) {
      case BenefitType.ACTIVITY:
        return t('tariffs.numberOfPeopleRequired');
      case BenefitType.VEHICLE_WITH_CHAUFFEUR:
        return t('tariffs.rentalDurationRequired');
    }
  }, [type, t]);

  const unit = useMemo(() => {
    switch (type) {
      case BenefitType.ACTIVITY:
        return t('tariffs.person');
      case BenefitType.VEHICLE_WITH_CHAUFFEUR:
        return t('tariffs.day');
    }
  }, [type, t]);

  const tarifUnit = useMemo(() => {
    switch (type) {
      case BenefitType.ACTIVITY:
        return tarifType === 'par-prestation'
          ? t('tariffs.perPrestationRequired')
          : !customTarif
            ? t('tariffs.perPersonRequired')
            : t('tariffs.adultRateRequired');
      case BenefitType.VEHICLE_WITH_CHAUFFEUR:
        return t('tariffs.perDayRequired');
      case BenefitType.VOL:
        return customTarif
          ? t('tariffs.adultRateRequired')
          : t('tariffs.rateRequired');
    }
  }, [customTarif, tarifType, type, t]);

  const addPartitionLabel = useMemo(() => {
    switch (type) {
      case BenefitType.ACTIVITY:
        return t('tariffs.addRepartition');
      case BenefitType.VEHICLE_WITH_CHAUFFEUR:
        return t('tariffs.addDuration');
    }
  }, [type, t]);

  return (
    <div className="flex flex-col gap-2">
      {fields.map((item, index) => (
        <div className="flex flex-col items-end" key={item.id}>
          {fields.length > 1 && !isViewMode && (
            <StyledButton
              title={t('common.delete')}
              className="p-0 h-auto"
              variant="link"
              onClick={() => remove(index)}
            />
          )}
          <div className="flex justify-between w-full flex-wrap md:flex-nowrap">
            {type !== BenefitType.VOL && (
              <div className="flex flex-col w-full gap-1.5">
                <Label className="text-gray-700">{label}</Label>
                <div className="flex gap-2 items-center flex-wrap md:flex-nowrap">
                  <Label className="text-gray-700">{t('common.from')}</Label>
                  <div className="w-16">
                    <StyledInput
                      control={control}
                      type="number"
                      name={`${fieldName}.${index}.from` as Path<FieldsType>}
                      min={getToValue(index - 1) || 1}
                      max={getToValue(index, -1)}
                      disabled={isViewMode}
                    />
                  </div>
                  {`${unit}(s)`}
                  <Label className="text-gray-700">{t('common.to')}</Label>
                  <div className="w-16">
                    <StyledInput
                      control={control}
                      type="number"
                      name={`${fieldName}.${index}.to` as Path<FieldsType>}
                      min={getFromValue(index) || 1}
                      max={getFromValue(index + 1, -1)}
                      disabled={isViewMode}
                    />
                  </div>
                  {`${unit}s`}
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <StyledInput
                control={control}
                type="number"
                min={0}
                name={`${fieldName}.${index}.price` as Path<FieldsType>}
                title={tarifUnit}
                placeholder={t('tariffs.partitionPlaceholder')}
                disabled={isViewMode}
              />
              {tarifType === 'par-personne' && customTarif && (
                <StyledInput
                  control={control}
                  name={
                    `${fieldName}.${index}.specialPrice` as Path<FieldsType>
                  }
                  type="number"
                  min={0}
                  title={t('tariffs.childRateRequired')}
                  placeholder={t('tariffs.partitionPlaceholder')}
                  disabled={isViewMode}
                />
              )}
            </div>
          </div>
        </div>
      ))}
      {!isViewMode && type !== BenefitType.VOL && (
        <StyledButton
          className="text-brand-600 w-fit pl-0"
          title={addPartitionLabel ?? t('tariffs.addRepartition')}
          onClick={() => {
            const lastToValue = getToValue(fields.length - 1, 1);
            return append({
              price: undefined,
              from: lastToValue,
              to: lastToValue ? lastToValue + 1 : 1,
              specialPrice:
                customTarif && tarifType === 'par-prestation' ? 0 : undefined,
            } as FieldArray<FieldsType, ArrayPath<FieldsType>>[][0]);
          }}
          variant="link"
          icon={<PlusIcon className="size-5 fill-brand-600" />}
        />
      )}
    </div>
  );
};

export default NestedTarifRepartition;
